import { SITE_CONFIG } from "@/config/constants";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
  /** Raw HTML content for complex sections with nested lists, links, etc. */
  rawHtml?: string;
  /** Subsections inside a section (e.g. 2.1, 2.2) */
  subsections?: Array<{
    heading: string;
    paragraphs?: string[];
    items?: string[];
  }>;
}

export interface LegalDocumentContent {
  title: string;
  closeLabel: string;
  lastUpdated: string;
  sections: LegalSection[];
}

export interface AccessibilityStatementSections {
  compliance: LegalSection;
  assessment: LegalSection;
  feedback: LegalSection;
  procedure: LegalSection;
  complaints: LegalSection;
  architectural: LegalSection;
  exclusions: LegalSection;
  mobileApps: LegalSection;
  additional: LegalSection;
}

export interface AccessibilityStatementContent {
  title: string;
  closeLabel: string;
  introLead: string;
  introWebsiteLabel: string;
  introWebsiteUrl: string;
  publicationDate: string;
  lastUpdated: string;
  preparationDate: string;
  sections: AccessibilityStatementSections;
  ombudsmanName: string;
  ombudsmanAddress: string;
  ombudsmanCity: string;
  ombudsmanWebsiteLabel: string;
  ombudsmanWebsiteUrl: string;
  ombudsmanFormLabel: string;
  ombudsmanFormUrl: string;
  deadlineCallout: {
    heading: string;
    text: string;
  };
  a11yFeatures: string[];
  techStandards: string[];
  browserShortcuts: string[];
}

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------

export const privacyPolicyContent: LegalDocumentContent = {
  title: "Polityka Prywatności",
  closeLabel: "Zamknij politykę prywatności",
  lastUpdated: "15 marca 2026",
  sections: [
    {
      heading: "1. Informacje ogólne",
      rawHtml: `<ol>
        <li>Niniejsza polityka dotyczy Serwisu www, funkcjonującego pod adresem url: <strong>https://bastunit.pl/</strong></li>
        <li>Operatorem serwisu oraz Administratorem danych osobowych jest: BastunIT Sebastian Kopiczko Jabłoniowa 22A/27, 80-175, Gdańsk – NIP: 844-222-05-47</li>
        <li>Adres kontaktowy poczty elektronicznej operatora: <a href="mailto:${SITE_CONFIG.email}">${SITE_CONFIG.email}</a></li>
        <li>Operator jest Administratorem Twoich danych osobowych w odniesieniu do danych podanych dobrowolnie w Serwisie.</li>
        <li>Serwis wykorzystuje dane osobowe w następujących celach:
          <ul>
            <li>Prowadzenie newslettera</li>
            <li>Obsługa zapytań przez formularz</li>
            <li>Realizacja zamówionych usług</li>
            <li>Prezentacja oferty lub informacji</li>
          </ul>
        </li>
        <li>Serwis realizuje funkcje pozyskiwania informacji o użytkownikach i ich zachowaniu w następujący sposób:
          <ol>
            <li>Poprzez dobrowolnie wprowadzone w formularzach dane, które zostają wprowadzone do systemów Operatora.</li>
            <li>Poprzez zapisywanie w urządzeniach końcowych plików cookie (tzw. „ciasteczka").</li>
          </ol>
        </li>
      </ol>`,
    },
    {
      heading: "2. Wybrane metody ochrony danych stosowane przez Operatora",
      rawHtml: `<ol>
        <li>Miejsca logowania i wprowadzania danych osobowych są chronione w warstwie transmisji (certyfikat SSL). Dzięki temu dane osobowe i dane logowania, wprowadzone na stronie, zostają zaszyfrowane w komputerze użytkownika i mogą być odczytane jedynie na docelowym serwerze.</li>
        <li>Operator okresowo zmienia swoje hasła administracyjne.</li>
        <li>W celu minimalizacji ryzyka nieuprawnionego dostępu od danych, Operator stosuje hasła złożone, zawierające małe i wielkie litery, cyfry oraz znaki specjalne, nie krótsze niż 8 znaków.</li>
        <li>W serwisie jest stosowana autentykacja dwuskładnikowa, co stanowi dodatkową formę ochrony logowania do Serwisu.</li>
        <li>Istotnym elementem ochrony danych jest regularna aktualizacja wszelkiego oprogramowania, wykorzystywanego przez Operatora do przetwarzania danych osobowych, co w szczególności oznacza regularne aktualizacje komponentów programistycznych.</li>
        <li>W celu ochrony danych Operator regularnie wykonuje kopie bezpieczeństwa.</li>
      </ol>`,
    },
    {
      heading: "3. Hosting",
      rawHtml: `<ol>
        <li>Serwis jest hostowany (technicznie utrzymywany) na serwerze operatora: www.cloudflare.com</li>
        <li>Firma hostingowa w celu zapewnienia niezawodności technicznej prowadzi logi na poziomie serwera. Zapisowi mogą podlegać:
          <ul>
            <li>zasoby określone identyfikatorem URL (adresy żądanych zasobów – stron, plików),</li>
            <li>czas nadejścia zapytania,</li>
            <li>czas wysłania odpowiedzi,</li>
            <li>nazwę stacji klienta – identyfikacja realizowana przez protokół HTTP,</li>
            <li>informacje o błędach jakie nastąpiły przy realizacji transakcji HTTP,</li>
            <li>adres URL strony poprzednio odwiedzanej przez użytkownika (referer link) – w przypadku gdy przejście do Serwisu nastąpiło przez odnośnik,</li>
            <li>informacje o przeglądarce użytkownika,</li>
            <li>informacje o adresie IP,</li>
            <li>informacje diagnostyczne związane z procesem samodzielnego zamawiania usług poprzez rejestratory na stronie,</li>
            <li>informacje związane z obsługą poczty elektronicznej kierowanej do Operatora oraz wysyłanej przez Operatora.</li>
          </ul>
        </li>
      </ol>`,
    },
    {
      heading: "4. Twoje prawa i dodatkowe informacje o sposobie wykorzystania danych",
      rawHtml: `<ol>
        <li>W niektórych sytuacjach Administrator ma prawo przekazywać Twoje dane osobowe innym odbiorcom, jeśli będzie to niezbędne do wykonania zawartej z Tobą umowy lub do zrealizowania obowiązków ciążących na Administratorze. Dotyczy to takich grup odbiorców:
          <ul>
            <li>firma hostingowa na zasadzie powierzenia</li>
            <li>upoważnieni pracownicy i współpracownicy, którzy korzystają z danych w celu realizacji celu działania strony</li>
            <li>firmy, świadczące usługi marketingu na rzecz Administratora</li>
          </ul>
        </li>
        <li>Twoje dane osobowe przetwarzane przez Administratora nie dłużej, niż jest to konieczne do wykonania związanych z nimi czynności określonych osobnymi przepisami (np. o prowadzeniu rachunkowości). W odniesieniu do danych marketingowych dane nie będą przetwarzane dłużej niż przez 3 lata.</li>
        <li>Przysługuje Ci prawo żądania od Administratora:
          <ul>
            <li>dostępu do danych osobowych Ciebie dotyczących,</li>
            <li>ich sprostowania,</li>
            <li>usunięcia,</li>
            <li>ograniczenia przetwarzania,</li>
            <li>oraz przenoszenia danych.</li>
          </ul>
        </li>
        <li>Przysługuje Ci prawo do złożenia sprzeciwu w zakresie przetwarzania wskazanego w pkt 3.3 c) wobec przetwarzania danych osobowych w celu wykonania prawnie uzasadnionych interesów realizowanych przez Administratora, w tym profilowania, przy czym prawo sprzeciwu nie będzie mogło być wykonane w przypadku istnienia ważnych prawnie uzasadnionych podstaw do przetwarzania, nadrzędnych wobec Ciebie interesów, praw i wolności, w szczególności ustalenia, dochodzenia lub obrony roszczeń.</li>
        <li>Na działania Administratora przysługuje skarga do Prezesa Urzędu Ochrony Danych Osobowych, ul. Stawki 2, 00-193 Warszawa.</li>
        <li>Podanie danych osobowych jest dobrowolne, lecz niezbędne do obsługi Serwisu.</li>
        <li>W stosunku do Ciebie mogą być podejmowane czynności polegające na zautomatyzowanym podejmowaniu decyzji, w tym profilowaniu w celu świadczenia usług w ramach zawartej umowy oraz w celu prowadzenia przez Administratora marketingu bezpośredniego.</li>
        <li>Dane osobowe są przekazywane od krajów trzecich w rozumieniu przepisów o ochronie danych osobowych. Oznacza to, że przesyłamy je poza teren Unii Europejskiej.</li>
      </ol>`,
    },
    {
      heading: "5. Informacje w formularzach",
      rawHtml: `<ol>
        <li>Serwis zbiera informacje podane dobrowolnie przez użytkownika, w tym dane osobowe, o ile zostaną one podane.</li>
        <li>Serwis może zapisać informacje o parametrach połączenia (oznaczenie czasu, adres IP).</li>
        <li>Serwis, w niektórych wypadkach, może zapisać informację ułatwiającą powiązanie danych w formularzu z adresem e-mail użytkownika wypełniającego formularz. W takim wypadku adres e-mail użytkownika pojawia się wewnątrz adresu url strony zawierającej formularz.</li>
        <li>Dane podane w formularzu są przetwarzane w celu wynikającym z funkcji konkretnego formularza, np. w celu dokonania procesu obsługi zgłoszenia serwisowego lub kontaktu handlowego, rejestracji usług itp. Każdorazowo kontekst i opis formularza w czytelny sposób informuje, do czego on służy.</li>
      </ol>`,
    },
    {
      heading: "6. Logi Administratora",
      rawHtml: `<ol>
        <li>Informacje zachowaniu użytkowników w serwisie mogą podlegać logowaniu. Dane te są wykorzystywane w celu administrowania serwisem.</li>
      </ol>`,
    },
    {
      heading: "7. Istotne techniki marketingowe",
      rawHtml: `<ol>
        <li>Operator stosuje analizę statystyczną ruchu na stronie, poprzez Google Analytics (Google Inc. z siedzibą w USA). Operator nie przekazuje do operatora tej usługi danych osobowych, a jedynie zanonimizowane informacje. Usługa bazuje na wykorzystaniu ciasteczek w urządzeniu końcowym użytkownika. W zakresie informacji o preferencjach użytkownika gromadzonych przez sieć reklamową Google użytkownik może przeglądać i edytować informacje wynikające z plików cookies przy pomocy narzędzia: <a href="https://www.google.com/ads/preferences/">https://www.google.com/ads/preferences/</a></li>
        <li>Operator stosuje rozwiązanie automatyzujące działanie Serwisu w odniesieniu do użytkowników, np. mogące przesłać maila do użytkownika po odwiedzeniu konkretnej podstrony, o ile wyraził on zgodę na otrzymywanie korespondencji handlowej od Operatora.</li>
      </ol>`,
    },
    {
      heading: "8. Informacja o plikach cookies",
      rawHtml: `<ol>
        <li>Serwis korzysta z plików cookies.</li>
        <li>Pliki cookies (tzw. „ciasteczka") stanowią dane informatyczne, w szczególności pliki tekstowe, które przechowywane są w urządzeniu końcowym Użytkownika Serwisu i przeznaczone są do korzystania ze stron internetowych Serwisu. Cookies zazwyczaj zawierają nazwę strony internetowej, z której pochodzą, czas przechowywania ich na urządzeniu końcowym oraz unikalny numer.</li>
        <li>Podmiotem zamieszczającym na urządzeniu końcowym Użytkownika Serwisu pliki cookies oraz uzyskującym do nich dostęp jest operator Serwisu.</li>
        <li>Pliki cookies wykorzystywane są w następujących celach:
          <ol>
            <li>utrzymanie sesji użytkownika Serwisu (po zalogowaniu), dzięki której użytkownik nie musi na każdej podstronie Serwisu ponownie wpisywać loginu i hasła;</li>
            <li>realizacji celów określonych powyżej w części „Istotne techniki marketingowe";</li>
          </ol>
        </li>
        <li>W ramach Serwisu stosowane są dwa zasadnicze rodzaje plików cookies: „sesyjne" (session cookies) oraz „stałe" (persistent cookies). Cookies „sesyjne" są plikami tymczasowymi, które przechowywane są w urządzeniu końcowym Użytkownika do czasu wylogowania, opuszczenia strony internetowej lub wyłączenia oprogramowania (przeglądarki internetowej). „Stałe" pliki cookies przechowywane są w urządzeniu końcowym Użytkownika przez czas określony w parametrach plików cookies lub do czasu ich usunięcia przez Użytkownika.</li>
        <li>Oprogramowanie do przeglądania stron internetowych (przeglądarka internetowa) zazwyczaj domyślnie dopuszcza przechowywanie plików cookies w urządzeniu końcowym Użytkownika. Użytkownicy Serwisu mogą dokonać zmiany ustawień w tym zakresie. Przeglądarka internetowa umożliwia usunięcie plików cookies. Możliwe jest także automatyczne blokowanie plików cookies. Szczegółowe informacje na ten temat zawiera pomoc lub dokumentacja przeglądarki internetowej.</li>
        <li>Ograniczenia stosowania plików cookies mogą wpłynąć na niektóre funkcjonalności dostępne na stronach internetowych Serwisu.</li>
        <li>Pliki cookies zamieszczane w urządzeniu końcowym Użytkownika Serwisu wykorzystywane mogą być również przez współpracujące z operatorem Serwisu podmioty, w szczególności dotyczy to firm: Google (Google Inc. z siedzibą w USA), Facebook (Facebook Inc. z siedzibą w USA), Twitter (Twitter Inc. z siedzibą w USA).</li>
      </ol>`,
    },
    {
      heading: "9. Zarządzanie plikami cookies – jak w praktyce wyrażać i cofać zgodę?",
      rawHtml: `<ol>
        <li>Jeśli użytkownik nie chce otrzymywać plików cookies, może zmienić ustawienia przeglądarki. Zastrzegamy, że wyłączenie obsługi plików cookies niezbędnych dla procesów uwierzytelniania, bezpieczeństwa, utrzymania preferencji użytkownika może utrudnić, a w skrajnych przypadkach może uniemożliwić korzystanie ze stron www.</li>
        <li>W celu zarządzania ustawienia cookies wybierz z listy poniżej przeglądarkę internetową, której używasz i postępuj zgodnie z instrukcjami:
          <ul>
            <li><a href="https://support.microsoft.com/pl-pl/help/10607/microsoft-edge-view-delete-browser-history">Edge</a></li>
            <li><a href="https://support.microsoft.com/pl-pl/help/278835/how-to-delete-cookie-files-in-internet-explorer">Internet Explorer</a></li>
            <li><a href="http://support.google.com/chrome/bin/answer.py?hl=pl&amp;answer=95647">Chrome</a></li>
            <li><a href="http://support.apple.com/kb/PH5042">Safari</a></li>
            <li><a href="http://support.mozilla.org/pl/kb/W%C5%82%C4%85czanie%20i%20wy%C5%82%C4%85czanie%20obs%C5%82ugi%20ciasteczek">Firefox</a></li>
            <li><a href="http://help.opera.com/Windows/12.10/pl/cookies.html">Opera</a></li>
          </ul>
          <p>Urządzenia mobilne:</p>
          <ul>
            <li><a href="http://support.google.com/chrome/bin/answer.py?hl=pl&amp;answer=95647">Android</a></li>
            <li><a href="http://support.apple.com/kb/HT1677?viewlocale=pl_PL">Safari (iOS)</a></li>
            <li><a href="http://www.windowsphone.com/pl-pl/how-to/wp7/web/changing-privacy-and-other-browser-settings">Windows Phone</a></li>
          </ul>
        </li>
      </ol>`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------

export const termsOfServiceContent: LegalDocumentContent = {
  title: "Regulamin Świadczenia Usług",
  closeLabel: "Zamknij regulamin",
  lastUpdated: "15 marca 2026",
  sections: [
    {
      heading: "1. Postanowienia ogólne",
      paragraphs: [
        `1.1. Niniejszy Regulamin określa zasady współpracy pomiędzy ${SITE_CONFIG.name} Sebastian Kopiczko (zwanym dalej „Wykonawcą") a klientami korzystającymi z usług w zakresie dostępności cyfrowej.`,
        "1.2. Wykonawca prowadzi działalność w zakresie audytów dostępności cyfrowej, przygotowania deklaracji dostępności, konsultacji oraz wdrażania rozwiązań zgodnych z ustawą z dnia 4 kwietnia 2019 r. o dostępności cyfrowej stron internetowych i aplikacji mobilnych podmiotów publicznych.",
        "1.3. Niniejszy Regulamin obowiązuje od dnia jego publikacji na stronie internetowej.",
      ],
    },
    {
      heading: "2. Definicje",
      items: [
        "Klient – osoba fizyczna, prawna lub jednostka organizacyjna nieposiadająca osobowości prawnej, korzystająca z usług Wykonawcy.",
        "Usługa – audyt dostępności cyfrowej, przygotowanie deklaracji dostępności, konsultacje, szkolenia lub inne usługi świadczone przez Wykonawcę w zakresie dostępności cyfrowej.",
        "Umowa – pisemne lub elektroniczne porozumienie między Wykonawcą a Klientem określające zakres, termin i warunki świadczenia Usługi.",
        "WCAG – Web Content Accessibility Guidelines, międzynarodowy standard dostępności treści internetowych.",
      ],
    },
    {
      heading: "3. Nawiązanie współpracy",
      paragraphs: ["3.1. Klient może nawiązać współpracę poprzez:"],
      items: [
        "wypełnienie formularza kontaktowego na stronie internetowej",
        `wysłanie zapytania e-mail na adres: ${SITE_CONFIG.email}`,
        "kontakt telefoniczny",
      ],
    },
    {
      heading: "4. Zakres usług",
      paragraphs: ["4.1. Wykonawca świadczy usługi w zakresie:"],
      items: [
        "Audyty dostępności cyfrowej – przeprowadzanie testów zgodności stron internetowych i aplikacji mobilnych z WCAG 2.1 (poziom AA lub AAA) oraz ustawą o dostępności cyfrowej.",
        "Deklaracje dostępności – przygotowanie, aktualizacja i weryfikacja deklaracji dostępności zgodnie z wymogami ustawowymi.",
        "Procedury obsługi zgłoszeń – opracowanie schematów działania i szablonów odpowiedzi na żądania zapewnienia dostępności.",
        "Wsparcie w poprawkach – konsultacje techniczne dla zespołów IT w zakresie wdrażania poprawek dostępności.",
        "Dostępne multimedia – przygotowanie napisów, transkrypcji, audiodeskrypcji.",
        "Szkolenia – szkolenia dla koordynatorów dostępności, zespołów IT i redaktorów treści.",
      ],
    },
    {
      heading: "5. Obowiązki Wykonawcy",
      paragraphs: ["5.1. Wykonawca zobowiązuje się do:"],
      items: [
        "świadczenia Usług z należytą starannością, zgodnie z aktualną wiedzą i praktyką w zakresie dostępności cyfrowej",
        "dotrzymania uzgodnionych terminów realizacji",
        "zachowania poufności danych przekazanych przez Klienta",
        "dostarczenia raportów, dokumentacji i wytycznych w formie uzgodnionej z Klientem",
      ],
    },
    {
      heading: "6. Obowiązki Klienta",
      paragraphs: ["6.1. Klient zobowiązuje się do:"],
      items: [
        "przekazania Wykonawcy wszystkich niezbędnych informacji i dostępów do testowanych systemów (np. dane logowania, dokumentacja techniczna)",
        "terminowej zapłaty za świadczone Usługi zgodnie z Umową",
        "wyznaczenia osoby kontaktowej po stronie Klienta",
        "współpracy przy wdrażaniu rekomendacji i poprawek",
      ],
    },
    {
      heading: "7. Wynagrodzenie i płatności",
      paragraphs: [
        "7.1. Wysokość wynagrodzenia Wykonawcy określa każdorazowo Umowa lub akceptowana oferta.",
        "7.2. Wynagrodzenie płatne jest przelewem na rachunek bankowy Wykonawcy w terminie wskazanym na fakturze (zazwyczaj 14 lub 30 dni od daty wystawienia).",
        "7.3. W przypadku opóźnienia w płatności, Wykonawca może naliczyć odsetki ustawowe za zwłokę.",
        "7.4. Wykonawca zastrzega sobie prawo do wstrzymania realizacji Usługi do czasu uregulowania zaległych płatności.",
      ],
    },
    {
      heading: "8. Poufność",
      paragraphs: [
        "8.1. Wykonawca zobowiązuje się do zachowania w tajemnicy wszelkich informacji uzyskanych w trakcie realizacji Usługi, w tym danych osobowych, danych technicznych i biznesowych Klienta.",
        "8.2. Obowiązek zachowania poufności nie wygasa po zakończeniu współpracy.",
      ],
    },
    {
      heading: "9. Odpowiedzialność",
      paragraphs: [
        "9.1. Wykonawca ponosi odpowiedzialność za jakość świadczonych Usług zgodnie z przepisami prawa polskiego.",
        "9.2. Wykonawca nie ponosi odpowiedzialności za:",
      ],
      items: [
        "błędy lub niedociągnięcia powstałe w wyniku niewdrożenia rekomendacji przez Klienta",
        "zmiany w przepisach prawa lub standardach WCAG dokonane po zakończeniu realizacji Usługi",
        "problemy techniczne po stronie Klienta (np. hosting, infrastruktura IT)",
      ],
    },
    {
      heading: "10. Rozwiązanie umowy",
      paragraphs: [
        "10.1. Każda ze stron może rozwiązać Umowę za wypowiedzeniem z zachowaniem 14-dniowego okresu wypowiedzenia.",
        "10.2. Wykonawca może rozwiązać Umowę ze skutkiem natychmiastowym w przypadku:",
      ],
      items: [
        "opóźnienia Klienta w płatności przekraczającego 30 dni",
        "nieprzekazania niezbędnych informacji lub dostępów przez Klienta pomimo wezwania",
      ],
    },
    {
      heading: "11. Reklamacje",
      paragraphs: [
        "11.1. Klient może zgłosić reklamację dotyczącą jakości świadczonych Usług w ciągu 14 dni od ich zakończenia.",
        `11.2. Reklamację należy przesłać na adres: ${SITE_CONFIG.email}, opisując szczegółowo przedmiot reklamacji.`,
        "11.3. Wykonawca rozpatrzy reklamację w ciągu 14 dni roboczych i poinformuje Klienta o decyzji.",
      ],
    },
    {
      heading: "12. Postanowienia końcowe",
      paragraphs: [
        "12.1. W sprawach nieuregulowanych niniejszym Regulaminem zastosowanie mają przepisy Kodeksu cywilnego oraz innych ustaw obowiązujących w Rzeczypospolitej Polskiej.",
        "12.2. Wykonawca zastrzega sobie prawo do zmiany Regulaminu. Zmiany obowiązują od chwili ich publikacji na stronie internetowej.",
        "12.3. Wszelkie spory wynikłe z realizacji Umowy będą rozstrzygane przez sąd właściwy dla siedziby Wykonawcy.",
        `12.4. Kontakt: ${SITE_CONFIG.email}`,
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Accessibility Statement
// ---------------------------------------------------------------------------

export const accessibilityStatementContent: AccessibilityStatementContent = {
  title: "Deklaracja Dostępności",
  closeLabel: "Zamknij deklarację dostępności",
  introLead:
    "zobowiązuje się zapewnić dostępność swojej strony internetowej zgodnie z ustawą z dnia 4 kwietnia 2019 r. o dostępności cyfrowej stron internetowych i aplikacji mobilnych podmiotów publicznych. Niniejsza deklaracja dotyczy strony internetowej",
  introWebsiteLabel: "bastunit.pl",
  introWebsiteUrl: "https://bastunit.pl",
  publicationDate: "16 lutego 2026",
  lastUpdated: "15 marca 2026",
  preparationDate: "16 lutego 2026 r.",
  ombudsmanName: "Rzecznik Praw Obywatelskich",
  ombudsmanAddress: "ul. Senatorska 11/13",
  ombudsmanCity: "00-075 Warszawa",
  ombudsmanWebsiteLabel: "bip.brpo.gov.pl",
  ombudsmanWebsiteUrl: "https://bip.brpo.gov.pl/pl",
  ombudsmanFormLabel: "Jak zgłosić się do Rzecznika",
  ombudsmanFormUrl:
    "https://bip.brpo.gov.pl/pl/content/zlozenie-wniosku-do-rzecznika-praw-obywatelskich",
  deadlineCallout: {
    heading: "Termin realizacji żądania",
    text: "Rozpatrzymy Twoje żądanie niezwłocznie, nie później niż w ciągu 7 dni roboczych od dnia otrzymania. Jeśli dotrzymanie tego terminu nie jest możliwe, poinformujemy Cię o tym i wskażemy termin realizacji (nie dłuższy niż 2 miesiące od dnia wpływu żądania), podając przyczynę zwłoki.",
  },
  sections: {
    compliance: {
      heading: "Status zgodności z ustawą",
      paragraphs: [
        "Strona internetowa jest w pełni zgodna z ustawą z dnia 4 kwietnia 2019 r. o dostępności cyfrowej stron internetowych i aplikacji mobilnych podmiotów publicznych oraz z międzynarodowym standardem WCAG 2.1 na poziomie AA.",
        "Serwis został zaprojektowany i zbudowany zgodnie z najlepszymi praktykami dostępności cyfrowej, z uwzględnieniem potrzeb osób z różnymi rodzajami niepełnosprawności.",
      ],
    },
    assessment: {
      heading: "Data sporządzenia deklaracji i metoda oceny",
      paragraphs: [
        "Deklarację sporządzono na podstawie samooceny przeprowadzonej przez właściciela serwisu. Ocena dostępności została przeprowadzona z wykorzystaniem:",
      ],
      items: [
        "automatycznych narzędzi testowych (axe DevTools, Lighthouse, WAVE)",
        "testów manualnych z użyciem klawiatury",
        "testów z czytnikami ekranu (NVDA, JAWS, VoiceOver)",
        "testów w różnych przeglądarkach i na różnych urządzeniach",
        "weryfikacji kontrastu kolorów",
        "oceny zgodności z WCAG 2.1 poziom AA",
      ],
    },
    feedback: {
      heading: "Informacje zwrotne i dane kontaktowe",
      paragraphs: [
        "W przypadku problemów z dostępnością strony prosimy o kontakt:",
        "Każdy ma prawo do wystąpienia z żądaniem zapewnienia dostępności cyfrowej strony internetowej, aplikacji mobilnej lub jakiegoś ich elementu. Można także zażądać udostępnienia informacji za pomocą alternatywnego sposobu dostępu, na przykład przez odczytanie niedostępnego cyfrowo dokumentu, opisanie zawartości filmu bez audiodeskrypcji itp.",
      ],
    },
    procedure: {
      heading: "Procedura rozpatrywania żądań",
      paragraphs: ["Żądanie powinno zawierać:"],
      items: [
        "dane osoby zgłaszającej żądanie (imię, nazwisko, adres e-mail lub adres do korespondencji)",
        "wskazanie strony internetowej, aplikacji mobilnej lub elementu, którego dotyczy żądanie",
        "wskazanie sposobu kontaktu (preferowana forma odpowiedzi)",
      ],
    },
    complaints: {
      heading: "Skargi i odwołania",
      paragraphs: [
        "Na niedotrzymanie terminów lub brak odpowiedzi na żądanie można złożyć skargę do:",
      ],
    },
    architectural: {
      heading: "Dostępność architektoniczna",
      paragraphs: [
        `${SITE_CONFIG.name} prowadzi działalność w formie zdalnej. Spotkania z klientami odbywają się online lub w miejscach uzgodnionych indywidualnie, dostosowanych do potrzeb obu stron.`,
      ],
    },
    exclusions: {
      heading: "Wyłączenia i ograniczenia",
      paragraphs: [
        "Na dzień publikacji deklaracji nie stwierdzono żadnych wyłączeń ani ograniczeń dostępności. Strona została zaprojektowana i przetestowana pod kątem pełnej zgodności z WCAG 2.1 poziom AA.",
      ],
    },
    mobileApps: {
      heading: "Aplikacje mobilne",
      paragraphs: [
        `${SITE_CONFIG.name} nie udostępnia dedykowanych aplikacji mobilnych. Strona internetowa jest w pełni responsywna i dostosowana do urządzeń mobilnych.`,
      ],
    },
    additional: {
      heading: "Informacje dodatkowe",
      paragraphs: [
        "Strona nie korzysta z tłumacza języka migowego online.",
        "Deklaracja dostępności została sporządzona na podstawie art. 6 ustawy z dnia 4 kwietnia 2019 r. o dostępności cyfrowej stron internetowych i aplikacji mobilnych podmiotów publicznych oraz rozporządzenia Rady Ministrów z dnia 12 kwietnia 2022 r. w sprawie Krajowych Ram Interoperacyjności.",
      ],
    },
  },
  a11yFeatures: [
    "Nawigacja klawiaturą – wszystkie elementy są dostępne przy użyciu samej klawiatury",
    "Widoczny fokus – wyraźne zaznaczenie aktywnego elementu podczas nawigacji",
    "Semantyczny HTML – prawidłowa struktura dokumentu, nagłówki, landmarki",
    "Alternatywy tekstowe – opisy dla wszystkich grafik i elementów nietekstowych",
    "Kontrast kolorów – spełniający wymogi WCAG 2.1 poziom AA (minimum 4.5:1 dla tekstu)",
    "Responsywność – dostosowanie do różnych rozmiarów ekranu i urządzeń",
    "Zrozumiałe komunikaty – jasne informacje o błędach i sukcesach w formularzach",
    "Etykiety formularzy – wszystkie pola formularzy mają powiązane etykiety",
    'Pominięcie nawigacji \u2013 link \u201ePrzejdź do treści\u201d na początku strony',
    "Język strony – poprawnie zadeklarowany język polski (lang=\"pl\")",
  ],
  techStandards: [
    "WCAG 2.1 (Web Content Accessibility Guidelines) – poziom AA",
    "HTML5 – semantyczny język znaczników",
    "CSS3 – kaskadowe arkusze stylów",
    "JavaScript (React) – interaktywne komponenty",
    "ARIA (Accessible Rich Internet Applications) – atrybuty wspierające dostępność",
  ],
  browserShortcuts: [
    "Powiększenie strony – Ctrl + \"+\" (Windows) lub Cmd + \"+\" (Mac)",
    "Pomniejszenie strony – Ctrl + \"-\" (Windows) lub Cmd + \"-\" (Mac)",
    "Reset do 100% – Ctrl + \"0\" (Windows) lub Cmd + \"0\" (Mac)",
    "Czytnik ekranu – możesz użyć NVDA (Windows), JAWS (Windows) lub VoiceOver (Mac/iOS)",
  ],
};

// ---------------------------------------------------------------------------
// Convenience re-export for components that use the old legalContent shape
// ---------------------------------------------------------------------------

export const legalContent = {
  privacyPolicy: {
    title: privacyPolicyContent.title,
    closeLabel: privacyPolicyContent.closeLabel,
  },
  termsOfService: {
    title: termsOfServiceContent.title,
    closeLabel: termsOfServiceContent.closeLabel,
  },
  accessibilityStatement: {
    title: accessibilityStatementContent.title,
    closeLabel: accessibilityStatementContent.closeLabel,
  },
} as const;
